import React, { useEffect, useRef } from "react";
import { Modal, View, Pressable, Animated, Dimensions, ScrollView, StyleSheet } from "react-native";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomText from "../../common/general/CustomText/CustomText";
import { X } from "lucide-react";

interface DrawerProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: React.ReactNode;
}

const SCREEN_WIDTH = Dimensions.get("window").width;
const DRAWER_WIDTH = 672;

export function Drawer({ isOpen, onOpenChange, title, description, children }: DrawerProps) {
  const slideAnim = useRef(new Animated.Value(DRAWER_WIDTH)).current;

  useEffect(() => {
    Animated.timing(slideAnim, {
      toValue: isOpen ? 0 : DRAWER_WIDTH,
      duration: 250,
      useNativeDriver: true,
    }).start();
  }, [isOpen]);

  return (
    <Modal visible={isOpen} transparent animationType="none">
      <View style={[styles.overlay]}>
        {/* Backdrop */}
        <Pressable style={StyleSheet.absoluteFill} onPress={() => onOpenChange(false)} />

        {/* Drawer */}
        <Animated.View
          style={[
            styles.drawer,
            {
              transform: [{ translateX: slideAnim }],
              width: Math.min(DRAWER_WIDTH, SCREEN_WIDTH),
            },
          ]}>
          <View
            style={[
              {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexDirection: "row",
              },
              styles.header,
            ]}>
            {/* Header */}
            <View>
              <CustomText
                text={title}
                size="xxl"
                useTranslationText={false}
                textType="text"
                weight="ultraBold"
                role="heading"
                aria-level="1"
                style={{ color: "#101828" }}
              />
              {description && (
                <>
                  <CustomText
                    text={description}
                    size="s"
                    useTranslationText={false}
                    textType="text"
                    weight="normal"
                    role="heading"
                    aria-level="1"
                    style={{ color: "#344054", marginTop: 4 }}
                  />
                </>
              )}
            </View>
            <Pressable
              onPress={() => onOpenChange(false)}
              style={{ height: 40, width: 40, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <X style={{ height: 20, width: 20 }} />
            </Pressable>
          </View>

          {/* Content */}
          <ScrollView style={styles.content}>{children}</ScrollView>
          <View style={[styles.header, { borderTopColor: "#e5e7eb", borderTopWidth: 1 }]}>
            <CustomButton
              text="Close"
              accessibilityHint="Press to close the drawer"
              size="m"
              bold={true}
              styleType="primary"
              style={{ borderRadius: 32 }}
              onPress={() => onOpenChange(false)}
            />
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.1)",
    backdropFilter: "blur(4px)" as any,
    WebkitBackdropFilter: "blur(8px)" as any,
    flexDirection: "row",
    justifyContent: "flex-end",
  },

  drawer: {
    backgroundColor: "white",
    height: "100%",
    flexDirection: "column",
    borderLeftWidth: 1,
    borderLeftColor: "rgba(255, 255, 255, 0.2)",
    shadowColor: "#000",
    shadowOffset: { width: -10, height: 0 },
    shadowOpacity: 0.1,
    shadowRadius: 20,
    elevation: 20,
  },

  header: {
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
  },

  title: {
    fontSize: 20,
    fontWeight: "600",
    color: "#111827",
  },

  description: {
    fontSize: 14,
    color: "#6b7280",
    marginTop: 4,
  },

  content: {
    flex: 1,
    padding: 24,
  },
});
